/* global window */

import React from 'react'
import PropTypes from 'prop-types'

import FlatButton from 'material-ui/FlatButton'
import { connect } from 'react-redux'
import fileDownload from 'js-file-download'

import { NewEstimateDialog, EditEstimateDialog } from './EstimateDialog'
import EstimatesList from './EstimatesList'
import BillingFilters from '../common/BillingFilters'
import SendBillDialog from '../common/SendBillDialog'
import SendBillStatusDialog from '../common/SendBillStatusDialog'
import DuplicateBillDialog from '../common/DuplicateBillDialog'
import DownloadBillPDFStatusDialog from 'billing/common/DownloadBillPDFStatusDialog'
import EstimateToInvoiceConfirmationDialog from './EstimateToInvoiceConfirmationDialog'
import EstimateToInvoiceStatusDialog from './EstimateToInvoiceStatusDialog'
import CloudFunctionsApisUtils from 'infra/CloudFunctionsApisUtils'
import DbEstimateEditor from '../infra/DbEstimateEditor'
import EstimatesRepository from '../infra/EstimatesRepository'
import Utils from '../infra/Utils'

class Estimates extends React.Component {

  static propTypes = {
    teamId: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      openMode: 'edit',
      newEstimate: false,
      openEstimate: false,
      openSendEstimateDialog: false,
      showEstimateSendStatusDialog: false,
      showEstimateDuplicateDialog: false,
      showDownloadingPDFDialog: false,
      showEstimateToInvoiceConfirmationDialog: false,
      showEstimateToInvoiceStatusDialog: false,
      filters: {}
    }
  }

  openNewEstimateDialog = event => {
    event.preventDefault()
    this.setState({
      newEstimate: true
    })
  }

  handleEstimateViewRequested = selectedEstimate => {
    this.setState({
      openEstimate: true,
      openMode: 'preview',
      selectedEstimate
    })
  }

  handleEstimateEditRequested = selectedEstimate => {
    this.setState({
      openEstimate: true,
      openMode: 'edit',
      selectedEstimate
    })
  }

  handleEstimateDuplicateRequested = selectedEstimate => {
    this.setState({
      showEstimateDuplicateDialog: true,
      estimateDuplicateStatus: 'duplicating'
    })
    // Only copy estimate items and notes
    const copyOptions = { notes: true, items: true }
    CloudFunctionsApisUtils.duplicateEstimate(this.props.teamId, selectedEstimate, copyOptions)
      .then(result => {
        this.setState({
          showEstimateDuplicateDialog: false,
          openEstimate: true,
          openMode: 'edit',
          selectedEstimate: result.estimate
        })    
      })
      .catch(() => {
        this.setState({
          estimateDuplicateStatus: 'error'
        })
      })
  }

  handleDuplicateEstimateStatusDialogCloseRequested = () => {
    this.setState({
      showEstimateDuplicateDialog: false
    })
  }

  handleEstimateConvertToInvoiceRequested = selectedEstimate => {
    this.setState({
      showEstimateToInvoiceConfirmationDialog: true,
      selectedEstimate
    })
  }

  handleEstimateToInvoiceConfirmationDialogActionConfirmed = () => {
    this.setState({
      showEstimateToInvoiceConfirmationDialog: false,
      showEstimateToInvoiceStatusDialog: true,
      estimateToInvoiceConvertionStatus: 'converting'
    })
    CloudFunctionsApisUtils.convertEstimateToInvoice(this.props.teamId, this.state.selectedEstimate.id)
      .then(response => {
        if (response && response.invoice && response.invoice.number) {
          this.setState({ showEstimateToInvoiceStatusDialog: false })
          // Redirect to the invoices tab which will open the created invoice by default
          window.location = `#/app/invoices/?number=${response.invoice.number}`
        }
        else {
          this.setState({ estimateToInvoiceConvertionStatus: 'error' })
        }
      })
      .catch(() => { this.setState({ estimateToInvoiceConvertionStatus: 'error' }) })
  }

  handleEstimateToInvoiceConfirmationDialogCloseRequested = () => {
    this.setState({ showEstimateToInvoiceConfirmationDialog: false })
  }

  handleEstimateToInvoiceStatusDialogCloseRequested = () => {
    this.setState({ showEstimateToInvoiceStatusDialog: false })
  }

  handleEstimateDialogClose = () => {
    this.setState({
      newEstimate: false,
      openEstimate: false
    })
  }

  handleFilterChanged = filters => {
    this.setState({ filters })
  }

  handleSendEstimateSelected = estimate => {
    const estimateEditor = new DbEstimateEditor()
    estimateEditor.initialize({
      estimate,
      teamId: this.props.teamId
    })
    this.setState({
      estimateEditor,
      newEstimate: false,
      openEstimate: false,
      openSendEstimateDialog: true
    })
  }

  handleSendEstimateDialogCloseRequested = () => {
    this.setState({ openSendEstimateDialog: false })
  }

  handleSendEstimateClicked = emailInfo => {
    this.state.estimateEditor.release()
    this.setState({
      openSendEstimateDialog: false,
      showEstimateSendStatusDialog: true,
      estimateSendStatus: 'sending'
    })
    const estimateId = this.state.estimateEditor.getBillingEntity().id
    CloudFunctionsApisUtils.sendEstimateEmail(this.props.teamId, estimateId, emailInfo)
      .then(() => EstimatesRepository.updateEstimateStatusOnEmailSent(this.props.teamId, estimateId))
      .then(() => { this.setState({ estimateSendStatus: 'success' }) })
      .catch(() => { this.setState({ estimateSendStatus: 'error' }) })
  }

  handleSendEstimateStatusDialogCloseRequested = () => {
    this.state.estimateEditor.release()
    this.setState({
      showEstimateSendStatusDialog: false
    })
  }

  handleEstimateDownloadRequested = selectedEstimate => {
    this.setState({
      showDownloadingPDFDialog: true,
      selectedEstimate,
      downloadStatus: 'downloading'
    })
    CloudFunctionsApisUtils.downloadEstimatePdf(this.props.teamId, selectedEstimate.id)
      .then(({ content, headers }) => fileDownload(content,
        Utils.getFilenameFromHeadersOrDefault(headers, 'Estimate', selectedEstimate.number), 'application/pdf'))
      .then(() => { this.setState({ showDownloadingPDFDialog: false }) })
      .catch(() => { this.setState({ downloadStatus: 'error' }) })
  }

  handleDownloadEstimatePDFStatusDialogCloseRequested = () => {
    this.setState({ showDownloadingPDFDialog: false })
  }

  render() {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.top}>
            <div style={styles.title}>Estimates</div>
            <FlatButton
              style={{border: '1px solid #e2e2e2'}}
              labelStyle={{ color: '#4d6f7f' }}
              label='Create new estimate'
              backgroundColor='#fff'
              onTouchTap={this.openNewEstimateDialog}/>
          </div>
          <BillingFilters
            style={{ marginTop: 32 }}
            billingEntity='estimate'
            teamId={this.props.teamId}
            selectedTab='all'
            onFilterChanged={this.handleFilterChanged} />
          <EstimatesList
            style={{ marginTop: 32 }}
            filters={this.state.filters}
            teamId={this.props.teamId}
            onSendEstimateSelected={this.handleSendEstimateSelected}
            onEstimateViewRequested={this.handleEstimateViewRequested}
            onEstimateEditRequested={this.handleEstimateEditRequested}
            onEstimateDuplicateRequested={this.handleEstimateDuplicateRequested}
            onEstimateDownloadRequested={this.handleEstimateDownloadRequested}
            onEstimateConvertToInvoiceRequested={this.handleEstimateConvertToInvoiceRequested} />
        </div>
        <EditEstimateDialog
          open={this.state.openEstimate}
          teamId={this.props.teamId}
          mode={this.state.openMode}
          estimate={this.state.selectedEstimate}
          onSendEstimateSelected={this.handleSendEstimateSelected}
          onCloseRequested={this.handleEstimateDialogClose} />
        <NewEstimateDialog
          open={this.state.newEstimate}
          teamId={this.props.teamId}
          mode={'edit'}
          onSendEstimateSelected={this.handleSendEstimateSelected}
          onCloseRequested={this.handleEstimateDialogClose} />
        <SendBillDialog
          open={this.state.openSendEstimateDialog}
          billEditor={this.state.estimateEditor}
          billingEntity='estimate'
          onSendBillClicked={this.handleSendEstimateClicked}
          onCloseRequested={this.handleSendEstimateDialogCloseRequested} />
        <SendBillStatusDialog
          open={this.state.showEstimateSendStatusDialog}
          billingEntity='estimate'
          status={this.state.estimateSendStatus}
          onCloseRequested={this.handleSendEstimateStatusDialogCloseRequested} />
        <DuplicateBillDialog
          open={this.state.showEstimateDuplicateDialog}
          billingEntity='estimate'
          status={this.state.estimateDuplicateStatus}
          onCloseRequested={this.handleDuplicateEstimateStatusDialogCloseRequested} />
        <DownloadBillPDFStatusDialog
          open={this.state.showDownloadingPDFDialog}
          status={this.state.downloadStatus}
          billingEntity='Estimate'
          onCloseRequested={this.handleDownloadEstimatePDFStatusDialogCloseRequested} />
        <EstimateToInvoiceConfirmationDialog
          open={this.state.showEstimateToInvoiceConfirmationDialog}
          onActionConfirmed={this.handleEstimateToInvoiceConfirmationDialogActionConfirmed}
          onCloseRequested={this.handleEstimateToInvoiceConfirmationDialogCloseRequested} />
        <EstimateToInvoiceStatusDialog
          open={this.state.showEstimateToInvoiceStatusDialog}
          status={this.state.estimateToInvoiceConvertionStatus}
          onCloseRequested={this.handleEstimateToInvoiceStatusDialogCloseRequested} />
      </div>
    )
  }

}

const styles = {
  container: {
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    flex: 1
  },
  content: {
    marginLeft: 140,
    marginRight: 140,
    marginTop: 64
  },
  top: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  title: {
    fontSize: 24,
    fontWeight: 400
  }
}

const mapStateToProps = state => ({ teamId: state.firebase.team.metadata.value.id })

export default connect(mapStateToProps)(Estimates)



// WEBPACK FOOTER //
// ./src/billing/estimates/Estimates.js